import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { ApiError } from '@/api/http';
import { queryKeyRoots } from '@/api/queryKeys';
import { createQuestion, updateQuestion, type Question } from '@/api/questionSets';
import {
  CATEGORIES,
  DIFFICULTIES,
  isCategory,
  isDifficulty,
  type Category,
  type Difficulty,
} from '@/api/vocabularies';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Field } from '@/components/ui/Field';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';

import {
  changesOf,
  creationOf,
  fieldsOf,
  questionFormSchema,
  QUESTION_TEXT_MAX_LENGTH,
  type QuestionForm,
} from './questionFields';

/**
 * What a `Select` is given while nothing has been chosen. **Radix's own value for that state**, not a
 * sentinel of ours: `shouldShowPlaceholder(value)` is `value === '' || value === undefined`, and the
 * empty string is refused on an *item* precisely because it is reserved for the root. Passing it rather
 * than `undefined` is what keeps the control controlled for its whole life, which is the warning the
 * live pass measured (see the note beside the two values below).
 */
const NOTHING_CHOSEN = '';

interface Props {
  /**
   * The set that owns this question — the **address** of both routes and the write gate itself (ADR
   * 0019). It is required on both paths: there is no way to write a question outside a set, and there
   * is no set id in either body.
   */
  setId: number;
  /**
   * The question being edited, or **nothing at all** for a new one — which is what makes this one
   * dialog rather than two, exactly as `vacancy?: Role` does for §6.8's `VacancyDialog`. The fields
   * are identical on both paths, and a create form and an edit form that describe the same question
   * in two different ways is the drift `personFields.ts` was extracted to prevent.
   */
  question?: Question;
  onClose: () => void;
}

/**
 * §6.9's **Add question** and its **Edit** — one dialog, three fields (`text`, `category`,
 * `difficulty`).
 *
 * ⚠️ **Feedback protection #1 — the question-text snapshot (ADR 0004) — is what the edit path owes a
 * sentence to, and the frontend's whole duty here is not to lie about it.** `Score` carries
 * `question_text_snapshot` beside the foreign key, copied **when the interview is arranged** and never
 * rewritten, so editing a question changes the **bank** and changes nothing about an interview that
 * already exists. The guarantee is the server's and is not re-implemented here — the named guards are
 * `apps/interviews/tests/test_models.py::
 * test_editing_a_question_does_not_change_the_text_stored_in_old_scores`,
 * `apps/interviews/tests/test_create_api.py::
 * test_editing_the_bank_after_an_interview_starts_does_not_touch_it` and
 * `apps/interviews/tests/test_autosave_api.py::
 * test_a_save_after_the_question_was_reworded_leaves_the_frozen_wording_alone`, mapped as mandatory
 * scenario 6 in `backend/tests/test_feedback_integrity.py` (`.claude/rules/tests.md`).
 *
 * What the client can get wrong is the **claim**. An edit form that says nothing leaves an admin to
 * assume the obvious and false thing — that fixing a typo fixes it everywhere, in the interview
 * somebody filled in last month too — and a form that said *"changes apply everywhere"* would teach
 * something false about their own data in the one product whose argument is that recorded feedback
 * stays as recorded. So §6.9's sentence is rendered verbatim, with the half it leaves out said beside
 * it (`edit.snapshot.*`), and `questionWrites.test.tsx` walks the catalogue for any string promising
 * retroactive effect.
 *
 * ⚠️ **It renders on *edit* and not on *add*.** A new question has no recorded score and no interview
 * to keep a wording for, so the sentence would be reassurance about nothing on the one path where it
 * means nothing — and a sentence that appears everywhere stops being read anywhere. Both halves are
 * asserted, because only the pair makes the claim falsifiable.
 *
 * ⚠️ **`is_active` is offered nowhere in this form** (`questionFields.ts`): the archive is an action
 * (ADR 0005, the shape of ADR 0010), and neither `QuestionIn` nor `QuestionUpdate` carries the field.
 * `PATCH` sends **only what changed** (`changesOf`) and nothing at all when nothing did; `POST` sends
 * exactly `QuestionIn`'s three fields (`creationOf`) and never a tenant, never an `author` and never a
 * `set` — the session resolves the first two and the **address** carries the third
 * (`.claude/rules/tenancy.md`, ADR 0019).
 *
 * ⚠️ **Both paths write into one set, and this is the sprint's second decision made visible**
 * (2026-09-24, ADR 0019). A question belongs to exactly one set; two sets holding the same wording
 * hold **two questions**, and editing one does not change the other. So nothing in this dialog may say
 * a wording is shared, reused, or *"in N sets"* — there is no such fact to report, and reporting it
 * would teach the reader the model this sprint retracted.
 *
 * ⚠️ **Who may open either path is asked once, on the set, and never here** (2026-09-24, ADR 0019).
 * ~~**Add** is open to any of the three groups and **Edit** is the question author's or an `Admin`'s~~
 * — that pair is retracted: **there is one write gate and it is the set's** (ADR 0016 decision 5(c),
 * composing is writing the set). Any of the three groups may create a **set**; inside somebody's set,
 * add and edit are the same author-or-`Admin` question, asked by the screen and never by this dialog.
 * §5 decides the rest: hidden, never disabled. This dialog is never rendered for a viewer who may not
 * write the set it writes into.
 *
 * ⚠️ **This module said *"Admin only"* on both paths until Sprint 19**, standing on
 * `Route("post", BANK, …, (401, 403, 403, 201, 403, 403))` and the identical `patch QUESTION` row. The
 * route-scoped bank is gone and both rows with it; the claim is retracted rather than left standing.
 * **The client gate is never the evidence** either way.
 */
export function QuestionDialog({ setId, question, onClose }: Props) {
  const { t } = useTranslation(['questions', 'common', 'components']);
  const queryClient = useQueryClient();

  const editing = question !== undefined;

  const form = useForm<QuestionForm>({
    resolver: zodResolver(questionFormSchema),
    // Mounted when it opens and unmounted when it closes (`QuestionMenu`, the screen), so the question
    // as it is now *is* the default: no effect resets the form, and a half-typed wording cannot survive
    // a refetch that changed the row underneath it.
    //
    // ⚠️ A create starts with **no category and no difficulty chosen**, which `defaultValues` can say
    // because it is a `DeepPartial`: `QuestionIn` declares no default for either, so the form asks
    // rather than deciding (`questionFields.ts`).
    defaultValues: question ? fieldsOf(question) : { text: '' },
  });
  const { errors } = form.formState;
  const values = form.watch();

  /**
   * The two chosen values, **widened to what they are at runtime**. `z.infer` types them as `Category`
   * and `Difficulty` because the schema requires both, and before a choice is made they are
   * `undefined` — the honest type for a `DeepPartial` default.
   *
   * ⚠️ **`?? NOTHING_CHOSEN` is what keeps each `Select` controlled from its first render**, and it is a
   * `ui-reviewer` **live-pass finding** (2026-07-29): with `value={undefined}` the primary create path
   * logged *"Select is changing from uncontrolled to controlled"* — 0 on load, 0 on opening the dialog,
   * **1** on choosing a category and **2** on choosing a difficulty. Radix decides controlled-ness by
   * `prop !== undefined` (`@radix-ui/react-use-controllable-state`), so the first choice flips it and
   * warns.
   *
   * ⚠️ **This is not a sentinel the form has to strip: it is Radix's own idiom for *nothing chosen*.**
   * `shouldShowPlaceholder(value)` in `@radix-ui/react-select` is `value === '' || value === undefined`,
   * which is also why `SelectItem` refuses `value=""` — the empty string belongs to the root and means
   * the placeholder. So the empty string never names an option, never reaches `onValueChange` (which
   * only stores a value `isCategory`/`isDifficulty` recognises), and never reaches `POST`/`PATCH`:
   * `creationOf` and `changesOf` read the **form's** value, which stays `undefined` until a real choice
   * is made and is what `zod` refuses on submit (`form.errors.category`).
   */
  const category: Category | undefined = values.category;
  const difficulty: Difficulty | undefined = values.difficulty;

  const save = useMutation<Question, ApiError, QuestionForm>({
    mutationFn: (submitted) =>
      question
        ? updateQuestion(setId, question.id, changesOf(question, submitted))
        : createQuestion(setId, creationOf(submitted)),
    /**
     * ⚠️ **The set on both paths, and `roles` on the create — which is a reversal of what this
     * docblock said between 2026-08-11 and 2026-09-24** (ADR 0019). `queryKeyRoots.questionSets`
     * refetches the composition, so the new row appears where the API puts it (**last**) and the
     * order the reader sees is the server's rather than this client's guess; it also carries the set's
     * `active_question_count`, which is a column of the list row as well as of the detail.
     *
     * The intervening rule was *"a create must not touch `roles`"*, and its argument was sound for the
     * model it was written for: a new **library** entry belonged to no set, so no vacancy's
     * `active_question_count` could have moved. A question is now written **into a set** and is active
     * from the instant it exists, so the count of every vacancy that set is attached to is wrong until
     * it refetches — and the client cannot tell from any payload whether this set is attached
     * anywhere. An **edit** still moves no count: a rewording is not an arrival.
     *
     * Nothing is written into the cache by hand: a `setQueryData` here would put a row on screen the
     * server never confirmed, and the set's own refetch is what confirms the write landed.
     */
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeyRoots.questionSets });
      if (!editing) void queryClient.invalidateQueries({ queryKey: queryKeyRoots.roles });
      onClose();
    },
    /**
     * ⚠️ **A 404 is *"this question, or this set, is gone"*, and it is answered by re-reading rather
     * than under a field.** Both routes answer 404 for a set outside this reader's reach, a set in
     * another tenant, or an id that never existed — indistinguishable by design — so it is not a
     * refusal (that is a 403) and not something a form error could help with: there is no field to
     * correct. The set is asked again, which is what takes the row (or the whole screen) off, and the
     * dialog closes.
     *
     * ⚠️ **`POST` has a 404 to answer again, and it did not between Sprint 19 and ADR 0019.** The
     * library route took no path parameter at all; this one takes the **set's**, so a create against a
     * set that has gone answers 404 exactly as an edit does, and both take the same path here.
     */
    onError: (error) => {
      if (!error.isNotFound) return;
      void queryClient.invalidateQueries({ queryKey: queryKeyRoots.questionSets });
      onClose();
    },
  });

  const submit = form.handleSubmit(async (submitted) => {
    // Nothing was touched, so there is nothing to write: an empty `PATCH` answers 200 and moves
    // `updated_at` for no reason (`EditPersonDialog`'s finding, on the third screen it applies to).
    if (question && Object.keys(changesOf(question, submitted)).length === 0) return onClose();

    try {
      await save.mutateAsync(submitted);
    } catch {
      // Held by the mutation and rendered below; rethrowing reaches only the console.
    }
  });

  /**
   * §8 inside a dialog: what failed, in words, never a status code and never the server's English
   * `detail` (§8, §11).
   *
   * ⚠️ **Offline gets this screen's own sentence rather than either shared one.**
   * `components:state.offline` **promised** *"changes will be sent when the connection returns"* until
   * BUG-017 split it into the failed-**read** sentence and `state.offlineWrite` for a failed write;
   * nothing queues this request, then or now.
   *
   * **Why this screen still keeps its own** rather than adopting `state.offlineWrite`: status 0 means no
   * answer arrived, so the client does not know whether the question was added — and on a `POST` a
   * confident retry is how one wording ends up in this set **twice**. So the sentence names **this
   * set** as the thing to reload before trying again, which is the check that prevents that duplicate;
   * a shared string can say *"reload the page"* and not which page.
   * `VacancyDialog` keeps its own for the same reason on §6.8's writes.
   *
   * ⚠️ **This paragraph named the *library* as the thing to reload until 2026-09-24**, one hunk from the
   * string it explains — `questions:dialog.offline` was corrected to *"Reload this set …"* in this
   * sprint's own pass and its reason was not. The noun is the set on both paths: the address of the
   * write is the set's, so the reader's check for the duplicate is this set's composition and there is
   * no wider list for them to look in.
   *
   * ⚠️ **The 409 is the one refusal on this screen a reader can act on, and it gets its own sentence**
   * (added 2026-09-24). `POST …/question-sets/{set_id}/questions/` answers 409 for exactly one reason —
   * the set has used every one of its `MAX_QUESTIONS_PER_SET` places — and the **status is the
   * discriminator** because there is nothing else to read: the document declares one 409 on this route,
   * none at all on the edit route, and a plain `ErrorResponse` envelope with no code field on either.
   * That is this repository's own precedent for a route with a single 409 (`ScheduleInterviewDialog`
   * branches on `status === 409` bare); `EditPersonDialog` reads a body **field** through `bodyWith`
   * only because its route has **two** 409s to tell apart, and inventing a code the API does not send
   * would be a mechanism with nothing behind it.
   *
   * ⚠️ **Why it cannot go on saying *"The question could not be added."*** That sentence was adequate
   * while the refusal was recoverable — remove one, add one — and this sprint is what made it
   * **terminal**: since ADR 0019 a removal **archives**, the row keeps its place so *Restore* can put it
   * back, and the bound is counted over the questions the set has *ever* held. So removing frees
   * nothing, the set is full for good, and the only true next step is another set. The string says that
   * and never the server's English `detail` (§8, §11, the rule this docblock opens with) — the words
   * belong in the catalogue, where they can be translated and swept.
   *
   * ⚠️ **The add path only.** The sentence is about where a *new* question can go; an edit is a
   * rewording of a row the set already holds, and telling its author to start another set would answer
   * a question they did not ask.
   */
  const failure = (() => {
    if (!save.error || save.error.isNotFound) return null;
    if (save.error.isOffline) return t('questions:dialog.offline');
    if (save.error.isThrottled) return t('components:state.throttled');
    if (!editing && save.error.status === 409) return t('questions:new.setIsFull');
    return t(editing ? 'questions:edit.failed' : 'questions:new.failed');
  })();

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        // Esc and the overlay cancel, and cancelling writes nothing. Radix owns the focus trap and the
        // key handling, and the amended `Dialog` (TASK-069) puts focus on the content and gives it back
        // to the opener — nothing here defeats either, and nothing here re-fixes it.
        if (!next) onClose();
      }}
      title={t(editing ? 'questions:edit.dialogTitle' : 'questions:new.dialogTitle')}
      description={t(editing ? 'questions:edit.description' : 'questions:new.description')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common:cancel')}
          </Button>
          <Button
            // Not `type="submit"`: this renders in the dialog's actions row, outside the `<form>`
            // below. Enter inside the form still submits, through the same handler.
            onClick={() => void submit()}
            isLoading={save.isPending}
          >
            {t(editing ? 'questions:edit.submit' : 'questions:new.submit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <form className="flex flex-col gap-4" onSubmit={(event) => void submit(event)} noValidate>
          {/* Errors render **below their field** through `Field`, never as a toast (§4 Forms). */}
          <Field
            label={t('questions:form.text')}
            required
            error={errors.text ? t('questions:form.errors.text') : undefined}
          >
            {/*
             * A `Textarea` and not an `Input`: a question is a sentence somebody reads out, and
             * `QuestionIn.text` admits 2000 characters — far more than fits into one line. It is
             * **controlled** from the form's own value on purpose — the primitive grows itself when its
             * `value` changes (§4), and left uncontrolled it would keep its three rows and hide the
             * wording being typed behind `overflow-hidden`.
             *
             * ⚠️ **`maxLength` is the document's own number**, derived in `questionFields.ts` and
             * asserted equal to `QuestionIn.properties.text.maxLength`, the way `AddendumDialog`
             * carries `ADDENDUM_MAX_TEXT`. This comment said *"the document declares no maximum to
             * enforce"* until BUG-058 published one.
             *
             * ⚠️ **Why the attribute here and deliberately *not* on §6.8's abbreviation** — the
             * comment cited TASK-072's mutation 12 for the opposite conclusion, and the two cases are
             * not the same one. There, the fifth character has to be **typeable** for its refusal to be
             * visible, because the form itself refuses it in words below the field (*"One to four
             * characters"*). Here nothing says anything: this dialog never renders the server's English
             * `detail` (§8, §11), so a 2100-character wording comes back as `questions:new.failed` —
             * a sentence naming neither the field nor the limit — and the write now fails where it used
             * to succeed. Stopping the writer at the field is the only thing that can tell them where
             * the wording ends.
             */}
            {(fieldProps) => (
              <Textarea
                {...fieldProps}
                {...form.register('text')}
                value={values.text}
                maxLength={QUESTION_TEXT_MAX_LENGTH}
              />
            )}
          </Field>

          <Field
            label={t('questions:form.category')}
            required
            error={errors.category ? t('questions:form.errors.category') : undefined}
          >
            {(fieldProps) => (
              <Select
                id={fieldProps.id}
                // The visible `Field` label carries the same words; the `aria-label` is what names the
                // trigger for certain, since a `<label for>` on a Radix trigger depends on the
                // implementation staying a labelable element (`BankFilters`' note).
                aria-label={t('questions:form.category')}
                // `NOTHING_CHOSEN` and not `undefined` — see the note above the two values.
                value={category ?? NOTHING_CHOSEN}
                // The same derived guard the address parser and the filters use (`isCategory`), so
                // nothing here is cast: a value this component cannot name is one it does not store.
                onValueChange={(next) =>
                  isCategory(next) && form.setValue('category', next, { shouldValidate: true })
                }
                placeholder={t('questions:form.categoryPlaceholder')}
                // The values are the document's (`CATEGORIES`) and the words are the catalogue's, so
                // `SYSTEM_DESIGN` is never what a person reads and a value added on the backend appears
                // here without an edit (`api/vocabularies.ts`).
                options={CATEGORIES.map((value) => ({
                  value,
                  label: t(`components:category.${value}`),
                }))}
              />
            )}
          </Field>

          <Field
            label={t('questions:form.difficulty')}
            required
            error={errors.difficulty ? t('questions:form.errors.difficulty') : undefined}
          >
            {(fieldProps) => (
              <Select
                id={fieldProps.id}
                aria-label={t('questions:form.difficulty')}
                value={difficulty ?? NOTHING_CHOSEN}
                onValueChange={(next) =>
                  isDifficulty(next) && form.setValue('difficulty', next, { shouldValidate: true })
                }
                placeholder={t('questions:form.difficultyPlaceholder')}
                options={DIFFICULTIES.map((value) => ({
                  value,
                  label: t(`components:difficulty.${value}`),
                }))}
              />
            )}
          </Field>
        </form>

        {/*
         * ⚠️ **ADR 0004's cost, paid here and only here** — see this module's header. §7 predicts the
         * edit somebody will attempt: *"drop the reassurance line as clutter"*. It is not clutter; it
         * is the one place in the product where an admin can be told that the bank moves on and the
         * record does not.
         *
         * Below the fields rather than above them: it is about what the change will and will not do,
         * which is worth reading with the new wording in front of you — and above them it would push
         * the first field down the dialog (§8: never a layout that jumps, on the screen that reads it).
         */}
        {/*
         * ⚠️ **The add path now carries *nothing* here, and the sentence it carried is retracted rather
         * than reworded** (2026-09-24, ADR 0019). It read: *"No interview asks it yet: a question
         * reaches an interview through a question set, and this one is in none. Add it to a set on the
         * Sets tab…"*. A question written on this screen is **in a set by construction** — the set is
         * the address of the route — so the next interview built from that set asks it, and the
         * sentence is now false on the only path it ever rendered on. `api/retractedClaims.test.ts`
         * holds it, and the two versions before it, as data: neither the categorical claim nor the
         * narrowed one may come back as a paraphrase.
         *
         * ⚠️ **What survives is the asymmetry itself, in the other direction.** ADR 0004's sentence
         * renders on **edit** and not on **add**, because a question being created has no recorded
         * score and no arranged interview to keep a wording for — and a sentence that appears
         * everywhere stops being read anywhere. Both halves are asserted, because only the pair makes
         * the claim falsifiable.
         */}
        {editing && (
          <div className="rounded border border-rule-control bg-paper-sunken p-3 text-sm text-ink-700">
            <p className="font-medium text-ink-900">{t('questions:edit.snapshot.kept')}</p>
            <p className="mt-1">{t('questions:edit.snapshot.fromNow')}</p>
          </div>
        )}

        {failure && (
          <p role="alert" className="text-sm text-negative">
            {failure}
          </p>
        )}
      </div>
    </Dialog>
  );
}
